import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FiActivity, FiClock, FiInfo } from "react-icons/fi";
import {
  Modal,
  ModalHeader,
  ModalBody,
} from "../../../../components/BaseUI/Modal";
import { formatDateTimeTrInAppTz } from "../../../../utils/appTime";
import "./StatusHistoryModal.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
const PAGE_SIZE = 100;

const SOURCE_LABEL = {
  mqtt: "MQTT",
  heartbeat_timeout: "Zaman aşımı",
};

function formatStayDuration(ms) {
  if (ms == null || ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const d = Math.floor(totalSeconds / 86400);
  const h = Math.floor((totalSeconds % 86400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const parts = [];
  if (d) parts.push(`${d}g`);
  if (h) parts.push(`${h}sa`);
  if (m) parts.push(`${m}dk`);
  if (s || !parts.length) parts.push(`${s}s`);
  return parts.join(" ");
}

function formatStatusTs(iso) {
  const raw = formatDateTimeTrInAppTz(iso);
  return raw.replace(/[,\s]+(?=\d{2}:\d{2}:\d{2})/, " • ");
}

function liveDurationMs(log, nowMs) {
  if (log.endedAt) return log.durationMs ?? 0;
  const started = new Date(log.startedAt).getTime();
  if (Number.isNaN(started)) return log.durationMs ?? 0;
  return Math.max(0, nowMs - started);
}

const StatusHistoryModal = ({ isOpen, onClose, machine, getToken }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [nowMs, setNowMs] = useState(Date.now());

  const machineId = machine?._id;
  const machineCode = machine?.code || "";

  const fetchLogs = useCallback(
    async ({ skip = 0, append = false } = {}) => {
      if (!machineId) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `${API_URL}/api/machine-live/${machineId}/status-logs`,
          {
            params: { limit: PAGE_SIZE, skip },
            headers: { Authorization: `Bearer ${getToken()}` },
          },
        );
        const next = res.data?.logs || [];
        setTotal(Number(res.data?.total) || 0);
        setLogs((prev) => (append ? [...prev, ...next] : next));
      } catch (err) {
        setError(
          err.response?.data?.message || "Durum geçmişi yüklenemedi.",
        );
        if (!append) {
          setLogs([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [machineId, getToken],
  );

  useEffect(() => {
    if (!isOpen) return;
    setLogs([]);
    setTotal(0);
    fetchLogs({ skip: 0 });
  }, [isOpen, machineId, fetchLogs]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isOpen]);

  const hasMore = logs.length < total;

  return (
    <Modal
      isOpen={isOpen}
      width="medium"
      scrollBehavior="inside"
      onClose={onClose}
      className="status-history-modal"
    >
      <ModalHeader>
        <div className="status-history-modal__header">
          <div className="status-history-modal__title-block">
            <span className="status-history-modal__icon" aria-hidden>
              <FiActivity size={18} />
            </span>
            <div>
              <div className="status-history-modal__title">Durum Geçmişi</div>
              {machineCode ? (
                <div className="status-history-modal__code">#{machineCode}</div>
              ) : null}
            </div>
          </div>
          <div className="status-history-modal__total">
            <FiActivity size={14} />
            <div>
              <span className="status-history-modal__total-label">
                TOPLAM LOG
              </span>
              <strong>{total}</strong>
            </div>
          </div>
        </div>
      </ModalHeader>
      <ModalBody showCloseButton={false}>
        {loading ? (
          <div className="status-history-modal__empty">Yükleniyor...</div>
        ) : error ? (
          <div className="status-history-modal__empty">{error}</div>
        ) : logs.length === 0 ? (
          <div className="status-history-modal__empty">
            Henüz durum kaydı yok.
          </div>
        ) : (
          <div className="status-history-modal__list">
            {logs.map((log) => {
              const isOnline = log.status === "online";
              const duration = formatStayDuration(liveDurationMs(log, nowMs));
              const sourceLabel =
                SOURCE_LABEL[log.source] || SOURCE_LABEL.mqtt;
              return (
                <div
                  key={log._id}
                  className={`status-history-card ${isOnline ? "online" : "offline"}`}
                >
                  <div className="status-history-card__top">
                    <span
                      className={`status-history-card__badge ${isOnline ? "online" : "offline"}`}
                    >
                      {isOnline ? "Çevrimiçi" : "Çevrimdışı"}
                    </span>
                    <span
                      className="status-history-card__info"
                      title={sourceLabel}
                    >
                      <FiInfo size={14} />
                    </span>
                  </div>
                  <div className="status-history-card__bottom">
                    <span className="status-history-card__time">
                      <FiClock size={13} />
                      {formatStatusTs(log.startedAt)}
                    </span>
                    <span className="status-history-card__duration">
                      {isOnline
                        ? "Çevrimiçi Kalma Süresi"
                        : "Çevrimdışı Kalma Süresi"}
                      : {duration}
                    </span>
                  </div>
                </div>
              );
            })}
            {hasMore ? (
              <button
                type="button"
                className="status-history-modal__more"
                disabled={loadingMore}
                onClick={() => fetchLogs({ skip: logs.length, append: true })}
              >
                {loadingMore ? "Yükleniyor..." : "Daha fazla"}
              </button>
            ) : null}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default StatusHistoryModal;
